import React from "react";
import { Calendar, FileText, User, BadgeInfo } from "lucide-react";
import { SCHEMAS, resolveKey } from "../../hooks/assetSchemaUtils";

const prettyDate = (v?: string) => {
  if (!v) return "";
  const iso = v.includes("T") ? v : `${v}T00:00:00.000Z`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const amcChipClass = (status?: string) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 border-green-200";
    case "inactive":
      return "bg-gray-100 text-gray-800 border-gray-200";
    case "expired":
      return "bg-red-100 text-red-800 border-red-200";
    case "pending":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};
const titleCaseStatus = (s?: string) => {
  if (!s || s === "not-applicable") return "Not Applicable";
  return s
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

interface ViewerProps {
  values: Record<string, any>;
  equipmentInventoryName?: string;
  title?: string;
  className?: string;
}

export const DynamicAssetFieldsViewer: React.FC<ViewerProps> = ({
  values,
  equipmentInventoryName,
  title = "Asset-Specific Information",
  className = "",
}) => {
  const key = resolveKey(equipmentInventoryName);
  if (!key || !SCHEMAS[key]) return null;

  const fields = SCHEMAS[key];
  const items = fields
    .map((f) => {
      let v = values[f.name];
      if (v == null || v === "") return null;

      if (f.type === "date") {
        const s = typeof v === "string" ? v : "";
        const display = prettyDate(s);
        if (!display) return null;
        v = display;
      }

      if (f.name === "amcStatus") {
        if (v === "not-applicable") return null;
        return {
          name: f.name,
          label: f.label,
          chip: true,
          text: titleCaseStatus(String(v)),
          className: amcChipClass(String(v)),
          icon: "status" as const,
        };
      }

      const icon =
        f.type === "date"
          ? ("calendar" as const)
          : f.name.toLowerCase().includes("vendor")
          ? ("user" as const)
          : ("file" as const);

      return { name: f.name, label: f.label, text: String(v), icon };
    })
    .filter(Boolean) as Array<
    | {
        name: string;
        label: string;
        text: string;
        icon: "calendar" | "user" | "file";
      }
    | {
        name: string;
        label: string;
        chip: true;
        text: string;
        className: string;
        icon: "status";
      }
  >;
  if (!items.length) return null;

  const Icon = ({
    type,
  }: {
    type: "calendar" | "user" | "file" | "status";
  }) => {
    switch (type) {
      case "calendar":
        return <Calendar size={18} className="text-orange-500" />;
      case "user":
        return <User size={18} className="text-gray-500" />;
      case "status":
        return <BadgeInfo size={18} className="text-gray-500" />;
      default:
        return <FileText size={18} className="text-gray-500" />;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it) => (
          <div key={it.name} className="flex items-center gap-3">
            <Icon type={it.icon} />
            <div>
              <p className="text-sm text-gray-500">{it.label}</p>
              {"chip" in it ? (
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${it.className}`}
                >
                  {it.text}
                </span>
              ) : (
                <p className="font-medium">{it.text}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DynamicAssetFieldsViewer;
